import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { UserFeedback } from "../api/types";

// Round 45 follow-up, item 4 — read-only list of what users submitted via
// Profile's Feedback entry (ic_faceback), POST /feedback, migration
// 0109. The only admin action here is marking one reviewed — there is no
// reply-to-user flow (not asked for in the client's item 4, and not
// invented here).
export function FeedbackPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } =
    usePagedList<UserFeedback>("/admin/feedback");
  const { showToast } = useToast();

  async function toggleReviewed(row: UserFeedback) {
    try {
      await apiRequest(`/admin/feedback/${row.id}`, {
        method: "PATCH",
        body: { is_reviewed: !row.is_reviewed },
      });
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update feedback", "error");
    }
  }

  const columns: Column<UserFeedback>[] = [
    {
      header: "User",
      render: (row) => <span className="font-medium text-ink">{row.profiles?.username ?? "Unknown user"}</span>,
    },
    { header: "Message", render: (row) => <span className="max-w-md whitespace-pre-wrap text-sm">{row.message}</span> },
    { header: "Submitted", render: (row) => new Date(row.created_at).toLocaleString() },
    {
      header: "Status",
      render: (row) => (row.is_reviewed ? <Badge tone="success">Reviewed</Badge> : <Badge tone="neutral">New</Badge>),
    },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <Button size="sm" variant="secondary" onClick={() => toggleReviewed(row)}>
          {row.is_reviewed ? "Mark unreviewed" : "Mark reviewed"}
        </Button>
      ),
    },
  ];

  return (
    <AppShell title="Feedback">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        Free-text feedback submitted by users from their Profile screen. There's no reply-to-user flow here — this is
        a read list plus a reviewed/unreviewed marker.
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No feedback submitted yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />
    </AppShell>
  );
}
